import { enhancedApi, type TaskResponse } from '@store/generated/rule-build';

/**
 * Feature-specific tag types for rule-build
 */
const RULE_BUILD_TAG_TYPES = ['Task', 'TaskList', 'Draft'] as const;

/**
 * Task status values for the rule build workflow
 */
export type TaskStatus = 'empty' | 'generating' | 'ready' | 'active' | 'saving' | 'completed' | 'failed';

/**
 * Check if task status is a terminal state (no longer generating)
 */
export const isTerminalStatus = (status: string | undefined): boolean => {
  return status === 'ready' || status === 'active' || status === 'completed' || status === 'saving' || status === 'failed';
};

/**
 * Check if task is still generating (should continue polling)
 */
export const isGenerating = (status: string | undefined): boolean => {
  return status === 'generating';
};

/**
 * Check if task failed
 */
export const isFailed = (status: string | undefined): boolean => {
  return status === 'failed';
};

/**
 * Extended task status response that includes draft rules
 * Note: The backend returns full TaskResponse, but OpenAPI generates TaskResponse2
 * which is a simpler type. We extend the endpoint to return the full type.
 */
export type TaskStatusResponse = TaskResponse;

/**
 * Rule Build API - extends the generated API with custom tagging
 *
 * Provides task and draft management for the rule builder workflow.
 */
export const ruleBuildApi = enhancedApi.enhanceEndpoints({
  addTagTypes: RULE_BUILD_TAG_TYPES,
  endpoints: {
    // List all tasks for current user
    getV2ProviderRulesBuild: {
      providesTags: result =>
        result
          ? [
              ...result.tasks.map(task => ({
                type: 'Task' as const,
                id: task.id,
              })),
              { type: 'TaskList' as const, id: 'LIST' },
            ]
          : [{ type: 'TaskList', id: 'LIST' }],
    },

    // Get single task status with drafts
    getV2ProviderRulesBuildByTaskIdStatus: {
      providesTags: (_result, _error, { taskId }) => [
        { type: 'Task' as const, id: taskId },
      ],
    },

    // Create empty task
    postV2ProviderRulesBuild: {
      invalidatesTags: [{ type: 'TaskList', id: 'LIST' }],
    },

    // Create task from document upload
    postV2ProviderRulesBuildFromDocument: {
      invalidatesTags: [{ type: 'TaskList', id: 'LIST' }],
    },

    // Create task from natural language prompt
    postV2ProviderRulesBuildFromPrompt: {
      invalidatesTags: [{ type: 'TaskList', id: 'LIST' }],
    },

    // Create task from existing rule
    postV2ProviderRulesBuildFromRuleByRuleId: {
      invalidatesTags: [{ type: 'TaskList', id: 'LIST' }],
    },

    // Add draft to task
    postV2ProviderRulesBuildByTaskIdDrafts: {
      invalidatesTags: (_result, _error, { taskId }) => [
        { type: 'Task', id: taskId },
      ],
    },

    // Update draft
    patchV2ProviderRulesBuildByTaskIdDraftsAndDraftId: {
      invalidatesTags: (_result, _error, { taskId }) => [
        { type: 'Task', id: taskId },
      ],
    },

    // Delete draft
    deleteV2ProviderRulesBuildByTaskIdDraftsAndDraftId: {
      invalidatesTags: (_result, _error, { taskId }) => [
        { type: 'Task', id: taskId },
      ],
    },

    // Save single draft as compliance rule
    postV2ProviderRulesBuildByTaskIdDraftsAndDraftIdSave: {
      invalidatesTags: (_result, _error, { taskId }) => [
        { type: 'Task', id: taskId },
      ],
    },

    // Save multiple drafts as compliance rules (batch)
    postV2ProviderRulesBuildByTaskIdSave: {
      invalidatesTags: (_result, _error, { taskId }) => [
        { type: 'Task', id: taskId },
        { type: 'TaskList', id: 'LIST' },
      ],
    },
  },
});

// Export hooks with user-friendly names
export const {
  // Task queries
  useGetV2ProviderRulesBuildQuery: useGetTasksQuery,
  useGetV2ProviderRulesBuildByTaskIdStatusQuery: useGetTaskStatusQuery,
  useLazyGetV2ProviderRulesBuildByTaskIdStatusQuery: useLazyGetTaskStatusQuery,

  // Task creation mutations
  usePostV2ProviderRulesBuildMutation: useCreateTaskMutation,
  usePostV2ProviderRulesBuildFromDocumentMutation: useCreateTaskFromDocumentMutation,
  usePostV2ProviderRulesBuildFromPromptMutation: useCreateTaskFromPromptMutation,
  usePostV2ProviderRulesBuildFromRuleByRuleIdMutation: useCreateTaskFromRuleMutation,

  // Draft mutations
  usePostV2ProviderRulesBuildByTaskIdDraftsMutation: useAddDraftMutation,
  usePatchV2ProviderRulesBuildByTaskIdDraftsAndDraftIdMutation: useUpdateDraftMutation,
  useDeleteV2ProviderRulesBuildByTaskIdDraftsAndDraftIdMutation: useDeleteDraftMutation,
  usePostV2ProviderRulesBuildByTaskIdDraftsAndDraftIdSaveMutation: useSaveDraftMutation,

  // Save mutations (batch)
  usePostV2ProviderRulesBuildByTaskIdSaveMutation: useSaveDraftsMutation,
} = ruleBuildApi;
